"use client";

import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useAuthContext} from "@/contexts/common/AuthContextProvider";
import {GailProductRoles} from "@/enums/user";
import {useOrg} from "@/hooks/useOrg";
import {useOrgOutcomesFlag} from "@/hooks/useOrgOutcomesEnabled";
import {usePermissions} from "@/hooks/usePermissions";
import {BASE_SUPPORT_URL} from "@/utils/common/baseUrls";
import {
  buildSearchableNavItems,
  type SearchableNavItem,
} from "@/utils/navigation/searchableRoutes";

export type {SearchableNavItem};

export function useSearchableNavItems(): SearchableNavItem[] {
  const {
    productRolesState,
    isAuthenticated,
    permissionsDegraded,
    provisioningPending,
    user,
  } = useAuthContext();
  const {productAccess} = useOrg();
  const {canAccessAny, permissions} = usePermissions();
  const {enabled: orgOutcomesEnabled} = useOrgOutcomesFlag();
  const {t} = useTranslation("routes");
  const {t: ct} = useTranslation("common");
  const isReportUser =
    productRolesState?.roles?.includes(GailProductRoles.ReportUser) ?? false;

  return useMemo(
    () =>
      buildSearchableNavItems({
        canAccessAny,
        isAuthenticated,
        agencyId: user?.agencyId,
        permissionsDegraded,
        provisioningPending,
        hasPermissions: permissions !== null,
        userCanUseGailGpt: productAccess.userCanUseGailGpt,
        userCanUseGailAgent: productAccess.userCanUseGailAgent,
        productAccessLoading: productAccess.loading ?? false,
        gailAgentEnabled: productAccess.gailAgentEnabled ?? false,
        isReportUser,
        orgOutcomesEnabled,
        t,
        ct,
        supportHref: BASE_SUPPORT_URL,
      }),
    [
      canAccessAny,
      isAuthenticated,
      user?.agencyId,
      permissionsDegraded,
      provisioningPending,
      permissions,
      productAccess.userCanUseGailGpt,
      productAccess.userCanUseGailAgent,
      productAccess.loading,
      productAccess.gailAgentEnabled,
      isReportUser,
      orgOutcomesEnabled,
      t,
      ct,
    ]
  );
}
